import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmberRounded as WarningIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { api, ConnectorInterestSlot } from '../../services/api';

export interface ConnectorInterestUser {
  name: string;
  mobile: string;
  email: string;
  cea: string;
}

interface SuperConnectorInterestDialogProps {
  open: boolean;
  onClose: () => void;
  user: ConnectorInterestUser;
  /** Called after a successful registration. */
  onRegistered?: () => void;
}

const ORANGE = '#ed8936';

/** The numbered terms, kept as data so the layout stays consistent. */
const getTerms = (feeLabel: string | null): Array<{ title: string; body: string }> => [
  {
    title: 'Use of a dedicated number',
    body: 'We strongly recommend connecting a separate business number that is not critical to your day-to-day operations. This helps minimise disruption to your business if the connected account is restricted or banned.',
  },
  {
    title: 'Responsible messaging',
    body: 'You are responsible for your contact list, message content, and sending frequency. You must promptly honour requests from recipients who no longer wish to receive messages.',
  },
  {
    title: 'No guarantee against restrictions',
    body: 'Super Connector cannot guarantee that your WhatsApp account will remain active or unrestricted. We are not responsible for restrictions, suspensions, bans, loss of message history, or other actions taken by WhatsApp or Meta in connection with your use of Super Connector.',
  },
  {
    title: 'In-person setup appointment',
    body: 'You are required to schedule an appointment and visit our office to complete the setup of Super Connector.',
  },
  {
    title: 'Setup fee (Upon Successful Setup)',
    body: feeLabel
      ? `You will be prompted to make a one-time payment of ${feeLabel} for the setup fee.`
      : 'You will be prompted to make a one-time payment for the setup fee.',
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SuperConnectorInterestDialog: React.FC<SuperConnectorInterestDialogProps> = ({
  open,
  onClose,
  user,
  onRegistered,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [slots, setSlots] = useState<ConnectorInterestSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [feeLabel, setFeeLabel] = useState<string | null>(null);
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [selectedStartIso, setSelectedStartIso] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successLabel, setSuccessLabel] = useState<string | null>(null);

  // Seed editable fields + reset transient state each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setAcknowledged(false);
    setMobile(user.mobile || '');
    setEmail(user.email || '');
    setSelectedDateKey('');
    setSelectedStartIso('');
    setError(null);
    setSuccessLabel(null);
    setSlotsLoading(true);
    setFeeLabel(null);
    api
      .getConnectorInterestSlots()
      .then((s) => setSlots(s))
      .catch((err) => {
        console.error('Failed to load slots:', err);
        setSlots([]);
      })
      .finally(() => setSlotsLoading(false));
    api
      .getSuperConnectorPaymentConfig()
      .then(({ amountCents }) => {
        setFeeLabel(
          `S$${(amountCents / 100).toLocaleString('en-SG', {
            minimumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
          })}`,
        );
      })
      .catch((err) => console.error('Failed to load Super Connector setup fee:', err));
  }, [open, user.mobile, user.email]);

  // Group slots by date for the two-step picker (date → time).
  const dateGroups = useMemo(() => {
    const map = new Map<string, { label: string; slots: ConnectorInterestSlot[] }>();
    for (const s of slots) {
      const g = map.get(s.dateKey);
      if (g) g.slots.push(s);
      else map.set(s.dateKey, { label: s.dateLabel, slots: [s] });
    }
    return Array.from(map.entries()).map(([key, v]) => ({ key, ...v }));
  }, [slots]);

  const timesForSelectedDate = useMemo(
    () => dateGroups.find((g) => g.key === selectedDateKey)?.slots ?? [],
    [dateGroups, selectedDateKey],
  );

  const mobileValid = mobile.trim().length >= 6;
  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = acknowledged && mobileValid && emailValid && Boolean(selectedStartIso) && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.registerConnectorInterest({
        name: user.name.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
        acknowledged: true,
        appointmentAt: selectedStartIso,
      });
      setSuccessLabel(result.appointment?.label || 'your selected slot');
      onRegistered?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to register interest. Please try again.';
      setError(message);
      // A taken slot means our list is stale — refresh it.
      api.getConnectorInterestSlots().then(setSlots).catch(() => undefined);
      setSelectedStartIso('');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, user.name, mobile, email, selectedStartIso, onRegistered]);

  const readOnlyFieldSx = {
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: 'var(--color-text-strong)',
      opacity: 0.85,
    },
  } as const;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)', pb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Super Connector
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              Terms of Use &amp; Acknowledgement
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5 }}>
        {successLabel ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: '#4caf50', mb: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Interest registered!
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.75)', mb: 0.5 }}>
              Your setup appointment is booked for
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ORANGE }}>
              {successLabel}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', mt: 2 }}>
              A confirmation with a calendar invite has been sent to your email.
            </Typography>
          </Box>
        ) : (
          <>
            {/* About */}
            <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 2 }}>
              <strong>Super Connector</strong> is a multimodal automated engagement tool that helps you manage
              prospective clients, organise your sales pipeline, and nurture leads through scheduled WhatsApp
              messages and other engagement features.
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)', mb: 2 }}>
              Before registering your interest, please read and acknowledge the following.
            </Typography>

            {/* WhatsApp risk callout */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.25,
                p: 1.75,
                mb: 2.5,
                borderRadius: 1.5,
                bgcolor: 'rgba(237, 137, 54, 0.10)',
                border: `1px solid ${ORANGE}55`,
              }}
            >
              <WarningIcon sx={{ color: ORANGE, mt: '2px' }} fontSize="small" />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ORANGE, mb: 0.25 }}>
                  WhatsApp account risk
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  Super Connector sends automated WhatsApp messages through a connected account. Automated or
                  high-volume messaging may result in message restrictions, temporary suspension, or permanent
                  banning of your account by WhatsApp or Meta.
                </Typography>
              </Box>
            </Box>

            {/* Numbered terms */}
            <Box component="ol" sx={{ pl: 0, m: 0, listStyle: 'none', counterReset: 'term' }}>
              {getTerms(feeLabel).map((t) => (
                <Box
                  component="li"
                  key={t.title}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    mb: 1.75,
                    counterIncrement: 'term',
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      bgcolor: 'rgba(var(--color-text-rgb), 0.08)',
                      color: ORANGE,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 13,
                      '&::before': { content: 'counter(term)' },
                    }}
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                      {t.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.78)', lineHeight: 1.6 }}>
                      {t.body}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2.5, borderColor: 'rgba(var(--color-text-rgb), 0.1)' }} />

            {/* Acknowledgement */}
            <FormControlLabel
              sx={{ alignItems: 'flex-start', ml: 0, mb: 2 }}
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  sx={{ pt: 0, pr: 1, color: ORANGE, '&.Mui-checked': { color: ORANGE } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  I have read and understood the terms above. I acknowledge the risks associated with automated
                  WhatsApp messaging and accept responsibility for my use of Super Connector. I also agree to
                  schedule an appointment and visit the office to complete the required setup.
                </Typography>
              }
            />

            {/* Details */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
              <TextField
                label="Name"
                value={user.name}
                disabled
                fullWidth
                size="small"
                sx={readOnlyFieldSx}
                helperText="Auto-filled"
              />
              <TextField
                label="CEA Number"
                value={user.cea}
                disabled
                fullWidth
                size="small"
                sx={readOnlyFieldSx}
                helperText="Auto-filled"
              />
              <TextField
                label="Mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                fullWidth
                size="small"
                error={mobile.length > 0 && !mobileValid}
                helperText={mobile.length > 0 && !mobileValid ? 'Enter a valid mobile' : 'Editable'}
              />
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                size="small"
                error={email.length > 0 && !emailValid}
                helperText={email.length > 0 && !emailValid ? 'Enter a valid email' : 'Editable'}
              />
            </Box>

            {/* Schedule picker */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <EventAvailableIcon fontSize="small" sx={{ color: ORANGE }} />
              Choose your setup appointment
            </Typography>

            {slotsLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading available slots…
                </Typography>
              </Box>
            ) : dateGroups.length === 0 ? (
              <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                No appointment slots are available right now. Please check back later.
              </Alert>
            ) : (
              <>
                <TextField
                  select
                  label="Date"
                  value={selectedDateKey}
                  onChange={(e) => {
                    setSelectedDateKey(e.target.value);
                    setSelectedStartIso('');
                  }}
                  fullWidth
                  size="small"
                  sx={{ mb: 1.5 }}
                >
                  {dateGroups.map((g) => (
                    <MenuItem key={g.key} value={g.key}>
                      {g.label}
                    </MenuItem>
                  ))}
                </TextField>

                {selectedDateKey && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {timesForSelectedDate.map((s) => {
                      const selected = s.startIso === selectedStartIso;
                      return (
                        <Chip
                          key={s.startIso}
                          label={s.timeLabel}
                          clickable
                          onClick={() => setSelectedStartIso(s.startIso)}
                          variant={selected ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: selected ? 700 : 500,
                            ...(selected
                              ? { bgcolor: ORANGE, color: '#1a1206', '&:hover': { bgcolor: ORANGE } }
                              : { borderColor: 'rgba(var(--color-text-rgb), 0.3)' }),
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              </>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)', px: 2.5, py: 1.5 }}>
        {successLabel ? (
          <Button onClick={onClose} variant="contained" fullWidth sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#d97b28' } }}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting} sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              variant="contained"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                bgcolor: ORANGE,
                color: '#1a1206',
                fontWeight: 700,
                '&:hover': { bgcolor: '#d97b28' },
              }}
            >
              I Agree and Register My Interest
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SuperConnectorInterestDialog;
